// frontend/src/pages/OvertimeMonitoring/components/EmployeeOvertimeDetailModal.js
import React, { useState, useEffect, useCallback, useMemo } from 'react';
import {
  X,
  Clock,
  TrendingUp,
  Calendar,
  AlertTriangle,
  Mail,
  ChevronDown,
  ChevronUp,
  User,
  MessageSquareText
} from 'lucide-react';
import Modal from '../../../components/UI/Modal';
import { StatusBadge } from '../../../components/UI/StatusBadge';
import { DepartmentTradeChips } from '../../../components/UI/DepartmentTradeChips';
import {
  formatDateShort,
  formatDate,
  getTodayInSingaporeTimezone,
} from '../../../utils/dateUtils';
import {
  ResponsiveContainer,
  BarChart,
  Bar,
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ReferenceLine,
  Cell
} from 'recharts';

// ─── Constants ──────────────────────────────────────────────────────────────────
const OT_LIMIT = 72;
const BAR_COLORS = {
  normal: '#3B82F6',      // blue-500
  rest_day: '#F59E0B',    // amber-500
  public_holiday: '#EF4444', // red-500
  nightwork: '#7C3AED',   // purple-600
};

// Mon-first to match the sibling RestDayMonitoring TimelineCalendar.
const CALENDAR_WEEK_DAYS = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];

// Heatmap saturation palette per day type, 5 tiers (light → deep).
// Anchored at 12h per-day max -- one shift's worth of OT fully saturates the
// cell. Spotting marathon shifts is the primary use case for daily intensity.
const CALENDAR_PALETTE = {
  normal:         ['bg-blue-100',   'bg-blue-200',   'bg-blue-300',   'bg-blue-400',   'bg-blue-500'],
  rest_day:       ['bg-amber-100',  'bg-amber-200',  'bg-amber-300',  'bg-amber-400',  'bg-amber-500'],
  public_holiday: ['bg-red-100',    'bg-red-200',    'bg-red-300',    'bg-red-400',    'bg-red-500'],
  nightwork:      ['bg-purple-100', 'bg-purple-200', 'bg-purple-300', 'bg-purple-400', 'bg-purple-500'],
};

// Map per-day OT hours to a 1-5 saturation tier. Tier 5 ≥ 9.6h (≈ a full
// extra shift). Math.ceil(h / 2.4) gives even buckets; cap at 5.
const intensityTierForHours = (hours) => {
  if (!hours || hours <= 0) return 0;
  return Math.min(5, Math.max(1, Math.ceil(hours / 2.4)));
};

// Resolve cell visual style from (dayType, hours). Higher tiers flip the
// numeric label to white for contrast.
const calendarCellStyle = (dayType, hours) => {
  const tier = intensityTierForHours(hours);
  if (tier === 0) {
    return { bg: 'bg-white', dayNumText: 'text-gray-400', valueText: 'text-gray-300' };
  }
  const palette = CALENDAR_PALETTE[dayType] || CALENDAR_PALETTE.normal;
  const bg = palette[tier - 1];
  const isDark = tier >= 4;
  return {
    bg,
    dayNumText: isDark ? 'text-white/85' : 'text-gray-600',
    valueText: isDark ? 'text-white' : 'text-gray-900',
  };
};

// ─── Helpers ────────────────────────────────────────────────────────────────────
const formatHoursMinutes = (decimalHours) => {
  if (!decimalHours || decimalHours === 0) return '0h';
  const hours = Math.floor(decimalHours);
  const minutes = Math.round((decimalHours - hours) * 60);
  if (minutes === 0) return `${hours}h`;
  return `${hours}h ${minutes}min`;
};

const dayTypeLabel = (type) => {
  switch (type) {
    case 'rest_day': return 'Rest Day';
    case 'public_holiday': return 'Public Holiday';
    default: return 'Normal';
  }
};

const dayTypeBadgeClass = (type) => {
  switch (type) {
    case 'rest_day': return 'bg-amber-100 text-amber-700';
    case 'public_holiday': return 'bg-red-100 text-red-700';
    default: return 'bg-blue-100 text-blue-700';
  }
};

// ─── Custom Tooltip for Daily Chart ─────────────────────────────────────────────
const DailyChartTooltip = ({ active, payload }) => {
  if (!active || !payload?.length) return null;
  const data = payload[0].payload;
  const capH = data.capEligibleOtHours || 0;
  const offCapH = data.nonCapRdPhOtHours || 0;
  // Off-cap PH / rest-day hours are premium-rate WORKED time, not OT. A PH
  // can fall on a working day; a rest day is the WST-defined non-working day
  // (Sunday for a 6-day worker) — they are distinct, so we phrase each by its
  // own day type rather than lumping them as "RD/PH".
  const offCapOnly = data.otHours > 0 && offCapH > 0 && capH <= 0;
  const workedVerb =
    data.dayType === 'public_holiday' ? 'Public holiday worked'
    : data.dayType === 'rest_day' ? 'Rest day worked'
    : 'Worked';
  return (
    <div className="bg-white border border-gray-200 rounded-lg shadow-lg p-3 text-xs">
      <p className="font-semibold text-gray-900">{formatDate(data.date)}</p>
      {offCapOnly ? (
        <p className="text-gray-600 mt-1">
          {workedVerb}: <span className="font-medium text-gray-900">{formatHoursMinutes(data.otHours)}</span>
          <span className="text-gray-400"> (off-cap)</span>
        </p>
      ) : (
        <>
          <p className="text-gray-600 mt-1">
            Payable OT: <span className="font-medium text-gray-900">{formatHoursMinutes(data.otHours)}</span>
          </p>
          {offCapH > 0 && (
            <>
              <p className="text-gray-600">
                Counts toward 72h: <span className="font-medium text-gray-900">{formatHoursMinutes(capH)}</span>
              </p>
              <p className="text-gray-600">
                Off-cap (paid): <span className="font-medium text-gray-900">{formatHoursMinutes(offCapH)}</span>
              </p>
            </>
          )}
        </>
      )}
      <p className="text-gray-600">
        Cumulative cap OT: <span className="font-medium text-gray-900">{formatHoursMinutes(data.cumulativeOt)}</span>
      </p>
      <span className={`inline-block mt-1 px-1.5 py-0.5 rounded text-[10px] font-medium ${dayTypeBadgeClass(data.dayType)}`}>
        {dayTypeLabel(data.dayType)}
      </span>
      {data.hasCustomNote && data.otReason && (
        <p className="mt-2 pt-2 border-t border-gray-100 text-gray-800 leading-snug max-w-[16rem]">
          <span className="block text-[10px] font-medium uppercase tracking-wide text-amber-700 mb-0.5">Worker note</span>
          {data.otReason.trim()}
        </p>
      )}
    </div>
  );
};

// ─── Custom Tooltip for Trend Chart ─────────────────────────────────────────────
const TrendChartTooltip = ({ active, payload }) => {
  if (!active || !payload?.length) return null;
  const data = payload[0].payload;
  return (
    <div className="bg-white border border-gray-200 rounded-lg shadow-lg p-3 text-xs">
      <p className="font-semibold text-gray-900">{data.label || data.yearMonth}</p>
      <p className="text-gray-600 mt-1">
        Total OT: <span className="font-medium text-gray-900">{formatHoursMinutes(data.totalHours)}</span>
      </p>
      {data.isAbove72Hours && (
        <p className="text-red-600 font-medium mt-0.5">Exceeded 72h limit</p>
      )}
    </div>
  );
};

// ─── Skeleton Loader ────────────────────────────────────────────────────────────
const ChartSkeleton = ({ height = 'h-48' }) => (
  <div className={`${height} bg-gray-50 rounded-lg animate-pulse flex items-end justify-center gap-1 p-4`}>
    {Array.from({ length: 14 }).map((_, i) => (
      <div
        key={i}
        className="bg-gray-200 rounded-t"
        style={{ width: '16px', height: `${20 + Math.random() * 60}%` }}
      />
    ))}
  </div>
);

// ─── Calendar Skeleton ──────────────────────────────────────────────────────────
const CalendarSkeleton = () => (
  <div className="grid grid-cols-7 gap-1 animate-pulse">
    {Array.from({ length: 7 }).map((_, i) => (
      <div key={`h-${i}`} className="h-3 bg-gray-100 rounded" />
    ))}
    {Array.from({ length: 35 }).map((_, i) => (
      <div key={`c-${i}`} className="h-14 bg-gray-100 rounded-lg" />
    ))}
  </div>
);

// ─── Calendar Grid Builder ──────────────────────────────────────────────────────
//
// Pure helper: given selectedMonth ('YYYY-MM'), the dailyBreakdown array
// (only days with OT > 0; rest are inferred as no-OT cells), the projected
// breach date, and "today" (SGT), produce the week-row matrix consumed by
// <DailyOtCalendar />. Mon-first to match RestDayMonitoring's calendar.
const buildOtCalendarGrid = ({ selectedMonth, dailyBreakdown, projectedBreachDate, todayStr, publicHolidayDates }) => {
  if (!selectedMonth) return null;
  const [yStr, mStr] = selectedMonth.split('-');
  const year = parseInt(yStr, 10);
  const month = parseInt(mStr, 10) - 1;
  if (Number.isNaN(year) || Number.isNaN(month)) return null;

  const firstOfMonth = new Date(year, month, 1);
  const lastOfMonth = new Date(year, month + 1, 0);

  // Mon-first weekday index: getDay() returns 0=Sun..6=Sat → remap to 0=Mon..6=Sun.
  const firstDow = (firstOfMonth.getDay() + 6) % 7;

  // Index OT data by ISO date for O(1) lookup per cell.
  const dataByDate = {};
  (dailyBreakdown || []).forEach((d) => { dataByDate[d.date] = d; });

  const phSet = new Set(publicHolidayDates || []);

  const cells = [];
  // Leading greys to align the 1st on its weekday column.
  for (let i = 0; i < firstDow; i++) {
    cells.push({ kind: 'pad', key: `lead-${i}` });
  }
  // In-month days.
  for (let day = 1; day <= lastOfMonth.getDate(); day++) {
    const dateStr =
      `${year}-${String(month + 1).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
    cells.push({
      kind: 'day',
      key: dateStr,
      date: dateStr,
      dayNum: day,
      data: dataByDate[dateStr] || null,
      isToday: dateStr === todayStr,
      isBreach: !!projectedBreachDate && dateStr === projectedBreachDate,
      isPublicHoliday: phSet.has(dateStr),
    });
  }
  // Trailing greys so the final week is complete.
  while (cells.length % 7 !== 0) {
    cells.push({ kind: 'pad', key: `trail-${cells.length}` });
  }

  // Group into 7-cell rows.
  const weeks = [];
  for (let i = 0; i < cells.length; i += 7) {
    weeks.push(cells.slice(i, i + 7));
  }
  return weeks;
};

// ─── Daily OT Calendar ──────────────────────────────────────────────────────────
//
// Month grid (Mon-Sun, 7 columns) showing per-day OT hours as heat-mapped
// cells, with the projected 72h breach date highlighted as a future cell.
// Empty / no-OT days render as clean neutral cells -- visible but quiet --
// so HR can see weekday patterns and sparseness directly. The bar chart
// below this still carries cross-day magnitude comparison.
const DailyOtCalendar = ({
  weeks,
  hasAnyOt,
  showBreachInThisMonth,
  selectedNoteDate,
  onSelectNoteDate,
}) => {
  if (!weeks) {
    return (
      <div className="h-48 flex items-center justify-center text-sm text-gray-400">
        No calendar data available
      </div>
    );
  }

  const selectedNote = selectedNoteDate
    ? weeks.flat().find((cell) => cell.kind !== 'pad' && cell.date === selectedNoteDate && cell.data?.hasCustomNote && cell.data?.otReason)
    : null;
  const hasAnyNote = weeks.some((week) =>
    week.some((cell) => cell.kind !== 'pad' && cell.data?.hasCustomNote && cell.data?.otReason)
  );

  return (
    <div>
      <div className="grid grid-cols-7 gap-1 mb-1">
        {CALENDAR_WEEK_DAYS.map((d) => (
          <div
            key={d}
            className="text-[10px] font-medium text-gray-500 text-center pb-0.5"
          >
            {d}
          </div>
        ))}
      </div>

      <div className="grid grid-cols-7 gap-1">
        {weeks.flatMap((week) =>
          week.map((cell) => {
            if (cell.kind === 'pad') {
              return (
                <div
                  key={cell.key}
                  className="h-14 rounded-lg bg-gray-50/60"
                  aria-hidden="true"
                />
              );
            }
            const { date, dayNum, data, isToday, isBreach, isPublicHoliday } = cell;
            const hasOt = !!data && data.otHours > 0;
            const dayType = data?.dayType || 'normal';
            const style = calendarCellStyle(dayType, data?.otHours || 0);
            const capH = data?.capEligibleOtHours || 0;
            const offCapH = data?.nonCapRdPhOtHours || 0;
            // Off-cap PH / rest-day hours are premium-rate WORKED time, not
            // OT. PH and rest day are distinct (a PH can be a working day),
            // so each is phrased by its own day type — never lumped as RD/PH.
            const offCapOnly = hasOt && offCapH > 0 && capH <= 0;
            const isWorkedSpecialDay =
              hasOt && (dayType === 'public_holiday' || dayType === 'rest_day');
            const workedVerb =
              dayType === 'public_holiday' ? 'Public holiday worked'
              : dayType === 'rest_day' ? 'Rest day worked'
              : 'Worked';

            // PH without OT gets a subtle red-tinted background
            const phNoOt = isPublicHoliday && !hasOt;
            const cellBg = phNoOt ? 'bg-red-50' : style.bg;
            const cellDayNumText = phNoOt ? 'text-red-400' : style.dayNumText;

            const hasNote = !!(data?.hasCustomNote && data.otReason);
            const isSelected = hasNote && selectedNoteDate === date;
            const noteText = hasNote ? data.otReason.trim() : '';
            const summaryParts = [
              formatDate(date),
              !hasOt
                ? (isPublicHoliday ? 'Public Holiday — No OT' : 'No OT')
                : offCapOnly
                  ? `${workedVerb}: ${formatHoursMinutes(data.otHours)} (paid, off-cap — not counted toward 72h)`
                  : offCapH > 0
                    ? `Payable ${formatHoursMinutes(data.otHours)} (${dayTypeLabel(dayType)}) — ${formatHoursMinutes(capH)} toward 72h + ${formatHoursMinutes(offCapH)} off-cap`
                    : `Payable OT ${formatHoursMinutes(data.otHours)} (${dayTypeLabel(dayType)})`,
              hasOt ? `Cumulative cap OT ${formatHoursMinutes(data.cumulativeOt)}` : null,
              isBreach ? 'Projected to hit 72h on this day' : null,
            ].filter(Boolean).join(' — ');
            const accessibleLabel = hasNote
              ? `${summaryParts}. Worker note: ${noteText}. ${isSelected ? 'Hide note' : 'Show note'}`
              : summaryParts;

            // Breach styling layered on top of heat-map fill: dashed red
            // outline + ⚠ when no OT logged yet (the typical case, since
            // breach is by definition a future date). A selected note day
            // takes the amber ring so HR can see which note is open.
            const ringClasses = [
              isSelected
                ? 'ring-2 ring-amber-500 ring-offset-1'
                : (isToday ? 'ring-2 ring-gray-900 ring-offset-1' : ''),
              isBreach ? 'outline outline-2 outline-dashed outline-red-500 outline-offset-[-2px]' : '',
            ].filter(Boolean).join(' ');

            const CellTag = hasNote ? 'button' : 'div';
            const interactiveProps = hasNote
              ? {
                  type: 'button',
                  onClick: () => onSelectNoteDate(isSelected ? null : date),
                  'aria-expanded': isSelected,
                  'aria-label': accessibleLabel,
                }
              : {
                  title: summaryParts,
                  'aria-label': accessibleLabel,
                };
            const isDarkCell = hasOt && intensityTierForHours(data.otHours) >= 4;

            return (
              <CellTag
                key={cell.key}
                className={`relative h-14 w-full rounded-lg border ${phNoOt ? 'border-red-200' : 'border-gray-200'} ${cellBg} ${ringClasses} flex flex-col items-center justify-center transition-colors ${hasNote ? 'p-0 cursor-pointer hover:ring-2 hover:ring-amber-400 hover:ring-offset-1 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-red-500 focus-visible:ring-offset-1' : ''}`}
                {...interactiveProps}
              >
                <span
                  className={`absolute top-1 left-1.5 text-[10px] font-semibold leading-none ${cellDayNumText}`}
                >
                  {dayNum}
                </span>
                {hasOt && (
                  <span className={`text-xs font-bold leading-none mt-1.5 ${style.valueText}`}>
                    {formatHoursMinutes(data.otHours)}
                  </span>
                )}
                {isWorkedSpecialDay && (
                  <span className={`text-[8px] font-semibold leading-none tracking-tight whitespace-nowrap mt-0.5 ${style.valueText} opacity-90`}>
                    {dayType === 'public_holiday' ? 'PH worked' : 'RD worked'}
                  </span>
                )}
                {phNoOt && !isBreach && (
                  <span className="text-[10px] font-semibold text-red-300 mt-1">PH</span>
                )}
                {hasNote && (
                  <MessageSquareText
                    className={`absolute top-1 right-1 w-3 h-3 ${isDarkCell ? 'text-amber-100' : 'text-amber-600'}`}
                    aria-hidden="true"
                  />
                )}
                {isBreach && !hasOt && (
                  <AlertTriangle className="w-4 h-4 text-red-500 mt-1.5" aria-hidden="true" />
                )}
              </CellTag>
            );
          })
        )}
      </div>

      {selectedNote ? (
        <div
          className="mt-3 rounded-lg border border-amber-200 bg-amber-50 p-3"
          role="region"
          aria-label={`Worker note for ${formatDate(selectedNote.date)}`}
        >
          <div className="flex items-start justify-between gap-3">
            <div className="min-w-0">
              <p className="text-xs font-semibold text-gray-900">
                {formatDate(selectedNote.date)}
                <span className="ml-2 font-medium text-gray-500">
                  {formatHoursMinutes(selectedNote.data.otHours)} · {dayTypeLabel(selectedNote.data.dayType)}
                </span>
              </p>
              <p className="mt-1 text-sm text-gray-900 leading-snug whitespace-pre-wrap">
                {selectedNote.data.otReason.trim()}
              </p>
            </div>
            <button
              type="button"
              onClick={() => onSelectNoteDate(null)}
              className="flex-shrink-0 text-xs font-medium text-amber-800 hover:text-amber-950 px-2 py-1 rounded-md hover:bg-amber-100 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-red-500 focus-visible:ring-offset-1"
            >
              Hide
            </button>
          </div>
        </div>
      ) : hasAnyNote ? (
        <p className="mt-2 text-xs text-gray-500">
          Click a day with a note icon to read what the worker wrote.
        </p>
      ) : null}

      {/* Legend -- explains every colour / marker that can appear above. */}
      <div className="flex flex-wrap items-center gap-x-3 gap-y-1 mt-3 text-[10px] text-gray-500">
        <span className="flex items-center gap-1">
          <span className="w-2.5 h-2.5 rounded-sm bg-blue-400" /> Normal OT
        </span>
        <span className="flex items-center gap-1">
          <span className="w-2.5 h-2.5 rounded-sm bg-amber-400" /> Rest Day OT
        </span>
        <span className="flex items-center gap-1">
          <span className="w-2.5 h-2.5 rounded-sm bg-red-400" /> Public Holiday OT
        </span>
        <span className="flex items-center gap-1">
          <span className="w-2.5 h-2.5 rounded-sm bg-red-50 border border-red-200" /> Public Holiday
        </span>
        <span className="flex items-center gap-1">
          <span className="w-2.5 h-2.5 rounded-sm bg-purple-400" /> Nightwork
        </span>
        <span className="flex items-center gap-1 ml-1">
          <span className="w-2.5 h-2.5 rounded-sm bg-white border-2 border-gray-900" /> Today
        </span>
        <span className="flex items-center gap-1">
          <MessageSquareText className="w-3 h-3 text-amber-600" aria-hidden="true" />
          Worker note
        </span>
        {showBreachInThisMonth && (
          <span className="flex items-center gap-1">
            <span className="w-2.5 h-2.5 rounded-sm bg-white outline outline-2 outline-dashed outline-red-500" />
            Projected 72h breach
          </span>
        )}
        <span className="flex items-center gap-1 ml-auto text-gray-400">
          Saturation = payable OT hours (cell cap 12h)
        </span>
      </div>

      {!hasAnyOt && (
        <p className="text-xs text-gray-400 italic mt-3 text-center">
          No overtime recorded this month — the calendar is rendered for context.
        </p>
      )}
    </div>
  );
};

// ─── Main Modal Component ───────────────────────────────────────────────────────
const EmployeeOvertimeDetailModal = ({
  isOpen,
  onClose,
  employee,           // Row data from table (instant display)
  isCurrentMonth,
  selectedMonth,
  getEmployeeDailyBreakdown,
  getEmployeeHistory,
}) => {
  const [dailyData, setDailyData] = useState(null);
  const [historyData, setHistoryData] = useState(null);
  const [loadingDaily, setLoadingDaily] = useState(true);
  const [loadingHistory, setLoadingHistory] = useState(true);
  const [showOtLog, setShowOtLog] = useState(false);
  const [notificationTargets, setNotificationTargets] = useState(null);
  const [selectedNoteDate, setSelectedNoteDate] = useState(null);

  // Fetch data when modal opens
  const fetchData = useCallback(async () => {
    if (!employee?.staffId) return;

    setLoadingDaily(true);
    setLoadingHistory(true);
    setSelectedNoteDate(null);

    // Fetch daily breakdown and history in parallel
    const [dailyResult, historyResult] = await Promise.all([
      getEmployeeDailyBreakdown(employee.staffId, selectedMonth),
      getEmployeeHistory(employee.staffId),
    ]);

    if (dailyResult) {
      setDailyData(dailyResult);
      setNotificationTargets(dailyResult.notificationTargets);
    }
    setLoadingDaily(false);

    if (historyResult) {
      setHistoryData(historyResult);
    }
    setLoadingHistory(false);
  }, [employee?.staffId, selectedMonth, getEmployeeDailyBreakdown, getEmployeeHistory]);

  useEffect(() => {
    if (isOpen && employee) {
      fetchData();
    }
    // Reset state when modal closes
    if (!isOpen) {
      setDailyData(null);
      setHistoryData(null);
      setLoadingDaily(true);
      setLoadingHistory(true);
      setShowOtLog(false);
      setNotificationTargets(null);
      setSelectedNoteDate(null);
    }
  }, [isOpen, employee, fetchData]);

  // Calendar grid for the Daily OT Breakdown section. Memoised so the 31
  // cells aren't rebuilt on every render (e.g. when the OT log row toggles).
  // ``projectedBreachDate`` is null for past months (forecast is only
  // computed for the current month server-side).
  // NOTE: These hooks must run before the early return below to satisfy the
  // rules-of-hooks ordering invariant.
  const todayStr = useMemo(() => getTodayInSingaporeTimezone(), []);
  const projectedBreachDate = employee?.forecast?.projectedBreachDate || null;
  const calendarWeeks = useMemo(
    () => buildOtCalendarGrid({
      selectedMonth,
      dailyBreakdown: dailyData?.dailyBreakdown || [],
      projectedBreachDate,
      todayStr,
      publicHolidayDates: dailyData?.publicHolidayDates || [],
    }),
    [selectedMonth, dailyData, projectedBreachDate, todayStr]
  );

  if (!isOpen || !employee) return null;

  const forecast = employee.forecast;

  // Build 12-month trend data for chart
  const trendData = (historyData?.monthlyHistory || [])
    .slice()
    .reverse()
    .map((record) => {
      try {
        const [y, m] = record.yearMonth.split('-');
        const d = new Date(parseInt(y), parseInt(m) - 1, 1);
        return {
          ...record,
          label: d.toLocaleDateString('en-SG', { month: 'short', year: '2-digit' }),
        };
      } catch {
        return { ...record, label: record.yearMonth };
      }
    });

  // Worker notes for the month -- the free-text remarks a worker typed when
  // declaring OT. Surfaced as a first-class section here (rather than a list
  // indicator) because the note only matters once HR is investigating this
  // specific worker. Most-recent-first so the latest justification is on top.
  const workerNotes = (dailyData?.dailyBreakdown || [])
    .filter((d) => d.hasCustomNote && d.otReason)
    .slice()
    .reverse();

  // Only mark the breach cell when the user is actually viewing the month
  // the breach falls in -- otherwise the legend entry would be a lie.
  const breachIsInSelectedMonth = !!(
    projectedBreachDate &&
    selectedMonth &&
    projectedBreachDate.startsWith(`${selectedMonth}-`)
  );

  // Build mailto for notification
  const buildMailto = () => {
    if (!notificationTargets) return '#';

    const recipients = [];
    if (notificationTargets.tradeLeader?.email) {
      recipients.push(notificationTargets.tradeLeader.email);
    }
    notificationTargets.leadTradeLeader?.forEach((ltl) => {
      if (ltl.email) recipients.push(ltl.email);
    });
    notificationTargets.deputyHod?.forEach((d) => {
      if (d.email) recipients.push(d.email);
    });
    notificationTargets.hod?.forEach((h) => {
      if (h.email) recipients.push(h.email);
    });

    if (recipients.length === 0) return null;

    const projectedHours = forecast?.projectedEomOt
      ? formatHoursMinutes(forecast.projectedEomOt)
      : `${employee.currentOtHours}h`;

    // Parse selected month for display
    let monthDisplay = selectedMonth;
    try {
      const [y, m] = selectedMonth.split('-');
      const d = new Date(parseInt(y), parseInt(m) - 1, 1);
      monthDisplay = d.toLocaleDateString('en-SG', { month: 'long', year: 'numeric' });
    } catch { /* fallback to raw */ }

    const subject = encodeURIComponent(
      `OT Alert: ${employee.fullName} (${employee.staffId}) projected ${projectedHours} by end of ${monthDisplay}`
    );

    const body = encodeURIComponent(
      `Hi,\n\n` +
      `This is to alert you that ${employee.fullName} (${employee.staffId}) from ${employee.department} ` +
      `is currently at ${formatHoursMinutes(employee.currentOtHours)} of overtime for ${monthDisplay}.\n\n` +
      `Based on the current trajectory, the projected end-of-month OT is ${projectedHours}, ` +
      `which ${forecast?.forecastStatus === 'projected_exceed' ? 'will exceed' : 'may approach'} the MOM 72-hour limit.\n\n` +
      `Please take the necessary action to manage this worker's overtime.\n\n` +
      `Key Details:\n` +
      `- Current OT: ${formatHoursMinutes(employee.currentOtHours)}\n` +
      `- Hours Remaining to Limit: ${formatHoursMinutes(employee.hoursRemaining)}\n` +
      `- OT Days This Month: ${employee.daysWithOt}\n` +
      (forecast?.projectedBreachDate ? `- Projected Breach Date: ~${formatDateShort(forecast.projectedBreachDate)}\n` : '') +
      `\nRegards,\nHR Portal OT Monitoring System`
    );

    return `mailto:${recipients.join(',')}?subject=${subject}&body=${body}`;
  };

  // Progress bar width
  const progressPercent = Math.min(100, (employee.currentOtHours / OT_LIMIT) * 100);
  const projectedPercent = forecast?.projectedEomOt
    ? Math.min(100, (forecast.projectedEomOt / OT_LIMIT) * 100)
    : progressPercent;

  const hasNotificationTargets = notificationTargets && (
    notificationTargets.tradeLeader?.email ||
    notificationTargets.leadTradeLeader?.some((ltl) => ltl.email) ||
    notificationTargets.deputyHod?.some(d => d.email) ||
    notificationTargets.hod?.some(h => h.email)
  );

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      size="lg"
      theme="admin"
      contentClassName="space-y-6"
      customHeader={(
        <div className="modal-header flex-shrink-0">
          <div className="flex items-center gap-3">
            <div className="w-12 h-12 bg-red-100 rounded-full flex items-center justify-center">
              <span className="text-sm font-bold text-red-600">
                {employee.firstName?.charAt(0)}{employee.lastName?.charAt(0)}
              </span>
            </div>
            <div>
              <h3 className="text-lg font-bold text-gray-900">{employee.fullName}</h3>
              <div className="flex flex-wrap items-center gap-x-2 gap-y-1 mt-0.5">
                <span className="text-sm text-gray-500">{employee.staffId}</span>
                <DepartmentTradeChips
                  department={employee.department}
                  trade={employee.trade}
                  layout="inline"
                />
                {dailyData?.employee?.workSchedule && (
                  <span className="inline-flex items-center gap-1 text-sm text-gray-500">
                    <Calendar className="w-3 h-3 flex-shrink-0" />
                    {dailyData.employee.workSchedule}
                  </span>
                )}
              </div>
            </div>
          </div>
          <button onClick={onClose} className="modal-close-admin" aria-label="Close">
            <X className="w-5 h-5" />
          </button>
        </div>
      )}
      footer={(
        <button onClick={onClose} className="btn btn-admin-outline">
          Close
        </button>
      )}
    >

            {/* ── 1. Forecast Summary (instant from row data) ──────── */}
            {isCurrentMonth && forecast && (
              <div className="bg-gray-50 rounded-xl p-5 border border-gray-200">
                <div className="flex items-center justify-between mb-4">
                  <h4 className="text-sm font-semibold text-gray-900 flex items-center">
                    <TrendingUp className="w-4 h-4 mr-2 text-red-600" />
                    OT Forecast
                  </h4>
                  <StatusBadge status={forecast.forecastStatus} label={forecast.forecastStatus === 'projected_exceed' ? 'Will Exceed' : undefined} />
                </div>

                {/* Mini metric cards */}
                <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-4">
                  <div className="bg-white rounded-lg p-3 border border-gray-100">
                    <p className="text-[11px] text-gray-500 uppercase tracking-wide">72h Cap OT</p>
                    <p className="text-lg font-bold text-gray-900">{formatHoursMinutes(employee.currentOtHours)}</p>
                  </div>
                  <div className="bg-white rounded-lg p-3 border border-gray-100">
                    <p className="text-[11px] text-gray-500 uppercase tracking-wide">Projected EOM</p>
                    <p className={`text-lg font-bold ${forecast.projectedEomOt > OT_LIMIT ? 'text-red-600' : 'text-gray-900'}`}>
                      {formatHoursMinutes(forecast.projectedEomOt)}
                    </p>
                  </div>
                  <div className="bg-white rounded-lg p-3 border border-gray-100">
                    <p className="text-[11px] text-gray-500 uppercase tracking-wide">Breach Date</p>
                    <p className="text-lg font-bold text-gray-900">
                      {forecast.projectedBreachDate
                        ? `~${formatDateShort(forecast.projectedBreachDate)}`
                        : '—'}
                    </p>
                  </div>
                  <div className="bg-white rounded-lg p-3 border border-gray-100">
                    <p className="text-[11px] text-gray-500 uppercase tracking-wide">Days Left</p>
                    <p className="text-lg font-bold text-gray-900">{forecast.workingDaysRemaining ?? '—'}</p>
                  </div>
                </div>

                {/* Progress bar with projected overlay */}
                <div className="mb-3">
                  <div className="flex justify-between text-xs text-gray-500 mb-1">
                    <span>{formatHoursMinutes(employee.currentOtHours)} cap OT of {OT_LIMIT}h limit</span>
                    <span>{Math.round(progressPercent)}%</span>
                  </div>
                  <div className="relative h-3 bg-gray-200 rounded-full overflow-hidden">
                    {/* Projected (behind, lighter) */}
                    {forecast.projectedEomOt > employee.currentOtHours && (
                      <div
                        className="absolute inset-y-0 left-0 rounded-full bg-red-200"
                        style={{ width: `${projectedPercent}%` }}
                      />
                    )}
                    {/* Actual (on top) */}
                    <div
                      className={`absolute inset-y-0 left-0 rounded-full ${
                        progressPercent > 100 ? 'bg-red-500' :
                        progressPercent > 83 ? 'bg-orange-500' :
                        progressPercent > 67 ? 'bg-amber-500' :
                        'bg-blue-500'
                      }`}
                      style={{ width: `${progressPercent}%` }}
                    />
                  </div>
                  {forecast.projectedEomOt > employee.currentOtHours && (
                    <p className="text-[10px] text-gray-400 mt-1">
                      <span className="inline-block w-2 h-2 rounded-full bg-red-200 mr-1" />
                      Projected: {formatHoursMinutes(forecast.projectedEomOt)}
                    </p>
                  )}
                </div>

                {/* Forecast detail metrics */}
                <div className="grid grid-cols-3 gap-3 text-xs">
                  <div>
                    <span className="text-gray-500">Frequency:</span>
                    <span className="ml-1 font-medium text-gray-700">
                      {forecast.otFrequency ? `${Math.round(forecast.otFrequency * 100)}%` : '—'} of working days
                    </span>
                  </div>
                  <div>
                    <span className="text-gray-500">Intensity:</span>
                    <span className="ml-1 font-medium text-gray-700">
                      {forecast.otIntensity ? `${formatHoursMinutes(forecast.otIntensity)} / OT day` : '—'}
                    </span>
                  </div>
                  <div>
                    <span className="text-gray-500">Confidence:</span>
                    <span className="ml-1 font-medium text-gray-700">
                      {forecast.confidence != null ? `${Math.round(forecast.confidence * 100)}%` : '—'}
                    </span>
                  </div>
                </div>
              </div>
            )}

            {/* ── Non-current month: simple summary ─────────────────── */}
            {!isCurrentMonth && (
              <div className="bg-gray-50 rounded-xl p-5 border border-gray-200">
                <h4 className="text-sm font-semibold text-gray-900 flex items-center mb-3">
                  <Clock className="w-4 h-4 mr-2 text-blue-600" />
                  Month Summary
                </h4>
                <div className="grid grid-cols-3 gap-4">
                  <div className="bg-white rounded-lg p-3 border border-gray-100">
                    <p className="text-[11px] text-gray-500 uppercase tracking-wide">72h Cap OT</p>
                    <p className="text-lg font-bold text-gray-900">{formatHoursMinutes(employee.currentOtHours)}</p>
                  </div>
                  <div className="bg-white rounded-lg p-3 border border-gray-100">
                    <p className="text-[11px] text-gray-500 uppercase tracking-wide">Days with OT</p>
                    <p className="text-lg font-bold text-gray-900">{employee.daysWithOt}</p>
                  </div>
                  <div className="bg-white rounded-lg p-3 border border-gray-100">
                    <p className="text-[11px] text-gray-500 uppercase tracking-wide">Status</p>
                    <p className="text-lg font-bold">
                      <StatusBadge status={employee.unifiedStatus || employee.riskLevel || 'compliant'} label={(employee.unifiedStatus || employee.riskLevel) === 'projected_exceed' ? 'Will Exceed' : undefined} />
                    </p>
                  </div>
                </div>
              </div>
            )}

            {/* ── 2. Daily OT Breakdown ────────────────────────────────
                Calendar is the primary view: it exposes day-of-week
                patterns (rest-day OT vs Saturday OT vs Sunday OT) and
                gives the projected 72h breach date a spatial home next
                to the actual OT days. The bar chart is kept underneath
                in a compact form so cross-day magnitude comparison --
                the one thing the heatmap doesn't quite nail for
                adjacent days -- is still a glance away. */}
            <div className="bg-white rounded-xl border border-gray-200 p-5">
              <h4 className="text-sm font-semibold text-gray-900 flex items-center mb-3">
                <Calendar className="w-4 h-4 mr-2 text-blue-600" />
                Daily OT Breakdown
              </h4>

              {loadingDaily ? (
                <CalendarSkeleton />
              ) : (
                <DailyOtCalendar
                  weeks={calendarWeeks}
                  hasAnyOt={(dailyData?.dailyBreakdown || []).length > 0}
                  showBreachInThisMonth={breachIsInSelectedMonth}
                  selectedNoteDate={selectedNoteDate}
                  onSelectNoteDate={setSelectedNoteDate}
                />
              )}

              {/* Compact bar chart -- only render when there's actual OT
                  to compare. For sparse months the calendar already
                  carries the full picture and an empty bar chart would
                  be visual noise. */}
              {!loadingDaily && (dailyData?.dailyBreakdown || []).length > 0 && (
                <div className="mt-5 pt-3 border-t border-gray-100">
                  <p className="text-[10px] text-gray-400 uppercase tracking-wide mb-1.5">
                    Magnitude comparison
                  </p>
                  <div className="h-24">
                    <ResponsiveContainer width="100%" height="100%">
                      <BarChart data={dailyData.dailyBreakdown} margin={{ top: 5, right: 10, left: 0, bottom: 0 }}>
                        <CartesianGrid strokeDasharray="3 3" vertical={false} />
                        <XAxis
                          dataKey="date"
                          tickFormatter={(d) => {
                            try { return new Date(d + 'T00:00:00').getDate().toString(); } catch { return d; }
                          }}
                          tick={{ fontSize: 10 }}
                          interval={0}
                        />
                        <YAxis tick={{ fontSize: 10 }} />
                        <Tooltip content={<DailyChartTooltip />} />
                        <Bar dataKey="otHours" radius={[2, 2, 0, 0]} maxBarSize={20}>
                          {dailyData.dailyBreakdown.map((entry, idx) => (
                            <Cell key={idx} fill={BAR_COLORS[entry.dayType] || BAR_COLORS.normal} />
                          ))}
                        </Bar>
                      </BarChart>
                    </ResponsiveContainer>
                  </div>
                </div>
              )}
            </div>

            {/* ── 3. Worker Notes ──────────────────────────────────────
                Free-text remarks the worker attached when declaring OT,
                consolidated here so HR can read the "why" behind the month's
                overtime at a glance -- no expanding the log or hovering each
                calendar cell. Only days where the worker actually typed
                something appear. */}
            <div className="bg-white rounded-xl border border-gray-200 p-5">
              <h4 className="text-sm font-semibold text-gray-900 flex items-center mb-3">
                <MessageSquareText className="w-4 h-4 mr-2 text-amber-500" />
                Worker Notes
                {workerNotes.length > 0 && (
                  <span className="ml-2 text-xs font-medium text-amber-700 bg-amber-100 rounded-full px-2 py-0.5">
                    {workerNotes.length}
                  </span>
                )}
              </h4>

              {loadingDaily ? (
                <div className="animate-pulse space-y-2">
                  <div className="h-12 bg-gray-100 rounded-lg" />
                  <div className="h-12 bg-gray-100 rounded-lg" />
                </div>
              ) : workerNotes.length > 0 ? (
                <ul className="space-y-2">
                  {workerNotes.map((day, idx) => (
                    <li
                      key={idx}
                      className="flex items-start gap-3 rounded-lg border border-amber-100 bg-amber-50/60 p-3"
                    >
                      <div className="flex-shrink-0 text-center min-w-[3.5rem]">
                        <p className="text-xs font-semibold text-gray-900">{formatDateShort(day.date)}</p>
                        <span className={`inline-block mt-1 px-1.5 py-0.5 rounded text-[10px] font-medium ${dayTypeBadgeClass(day.dayType)}`}>
                          {dayTypeLabel(day.dayType)}
                        </span>
                      </div>
                      <p className="flex-1 text-sm italic leading-snug text-amber-900">
                        &ldquo;{day.otReason}&rdquo;
                      </p>
                      <span className="flex-shrink-0 text-xs font-medium text-gray-500">
                        {formatHoursMinutes(day.otHours)}
                      </span>
                    </li>
                  ))}
                </ul>
              ) : (
                <p className="text-sm italic text-gray-400">
                  No notes left by this worker for this month.
                </p>
              )}
            </div>

            {/* ── 4. 12-Month Trend ────────────────────────────────── */}
            <div className="bg-white rounded-xl border border-gray-200 p-5">
              <h4 className="text-sm font-semibold text-gray-900 flex items-center mb-3">
                <TrendingUp className="w-4 h-4 mr-2 text-purple-600" />
                12-Month OT Trend
              </h4>

              {loadingHistory ? (
                <ChartSkeleton height="h-40" />
              ) : trendData.length > 0 ? (
                <div className="h-40">
                  <ResponsiveContainer width="100%" height="100%">
                    <LineChart data={trendData} margin={{ top: 5, right: 10, left: 0, bottom: 0 }}>
                      <CartesianGrid strokeDasharray="3 3" />
                      <XAxis dataKey="label" tick={{ fontSize: 10 }} interval={0} />
                      <YAxis tick={{ fontSize: 10 }} />
                      <Tooltip content={<TrendChartTooltip />} />
                      <ReferenceLine
                        y={OT_LIMIT}
                        stroke="#EF4444"
                        strokeDasharray="6 6"
                        label={{ value: '72h Limit', position: 'insideTopRight', fontSize: 9, fill: '#DC2626' }}
                      />
                      <Line
                        type="monotone"
                        dataKey="totalHours"
                        stroke="#7C3AED"
                        dot={{ r: 3 }}
                        activeDot={{ r: 5 }}
                        strokeWidth={2}
                      />
                    </LineChart>
                  </ResponsiveContainer>
                </div>
              ) : (
                <div className="h-40 flex items-center justify-center text-sm text-gray-400">
                  No historical data available
                </div>
              )}
            </div>

            {/* ── 5. OT Day Log (Collapsible) ──────────────────────── */}
            <div className="bg-white rounded-xl border border-gray-200">
              <button
                onClick={() => setShowOtLog(!showOtLog)}
                className="w-full flex items-center justify-between p-4 text-sm font-semibold text-gray-900 hover:bg-gray-50 transition-colors rounded-xl focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-red-500 focus-visible:ring-offset-1"
              >
                <span className="flex items-center">
                  <Clock className="w-4 h-4 mr-2 text-gray-600" />
                  OT Day Log ({dailyData?.dailyBreakdown?.length || 0} days)
                </span>
                {showOtLog ? <ChevronUp className="w-4 h-4 text-gray-400" /> : <ChevronDown className="w-4 h-4 text-gray-400" />}
              </button>

              {showOtLog && (
                <div className="border-t border-gray-100 max-h-60 overflow-y-auto">
                  {loadingDaily ? (
                    <div className="p-4 text-center text-sm text-gray-400">Loading...</div>
                  ) : dailyData?.dailyBreakdown?.length > 0 ? (
                    <table className="w-full text-xs">
                      <thead className="bg-gray-50 sticky top-0">
                        <tr>
                          <th className="px-4 py-2 text-left text-gray-600 font-medium">Date</th>
                          <th className="px-4 py-2 text-center text-gray-600 font-medium">Day Type</th>
                          <th className="px-4 py-2 text-right text-gray-600 font-medium">Payable OT</th>
                          <th className="px-4 py-2 text-right text-gray-600 font-medium">72h Cap Cum.</th>
                        </tr>
                      </thead>
                      <tbody>
                        {[...dailyData.dailyBreakdown].reverse().map((day, idx) => (
                          <React.Fragment key={idx}>
                            <tr className="border-t border-gray-50 table-row">
                              <td className="px-4 py-2 text-gray-900">
                                <span className="flex items-center gap-1.5">
                                  {formatDate(day.date)}
                                  {day.hasCustomNote && (
                                    <MessageSquareText className="w-3.5 h-3.5 text-amber-500 flex-shrink-0" title="Worker left a note" />
                                  )}
                                </span>
                              </td>
                              <td className="px-4 py-2 text-center">
                                <span className={`inline-block px-1.5 py-0.5 rounded text-[10px] font-medium ${dayTypeBadgeClass(day.dayType)}`}>
                                  {dayTypeLabel(day.dayType)}
                                </span>
                              </td>
                              <td className="px-4 py-2 text-right font-medium text-gray-900">
                                {formatHoursMinutes(day.otHours)}
                                {day.nonCapRdPhOtHours > 0 && (
                                  <div className="text-[10px] font-normal text-gray-500">
                                    {formatHoursMinutes(day.nonCapRdPhOtHours)} off-cap (paid)
                                  </div>
                                )}
                              </td>
                              <td className={`px-4 py-2 text-right font-medium ${day.cumulativeOt > OT_LIMIT ? 'text-red-600' : 'text-gray-600'}`}>
                                {formatHoursMinutes(day.cumulativeOt)}
                              </td>
                            </tr>
                            {day.hasCustomNote && day.otReason && (
                              <tr className="bg-amber-50/60">
                                <td colSpan={4} className="px-4 py-1.5">
                                  <p className="text-[11px] text-amber-800 leading-snug flex items-start gap-1.5">
                                    <MessageSquareText className="w-3 h-3 text-amber-500 mt-0.5 flex-shrink-0" />
                                    <span className="italic">&ldquo;{day.otReason}&rdquo;</span>
                                  </p>
                                </td>
                              </tr>
                            )}
                          </React.Fragment>
                        ))}
                      </tbody>
                    </table>
                  ) : (
                    <div className="p-4 text-center text-sm text-gray-400">
                      No overtime days recorded
                    </div>
                  )}
                </div>
              )}
            </div>

            {/* ── 5. Notify Management (current month only) ────────── */}
            {isCurrentMonth && (
              <div className="bg-white rounded-xl border border-gray-200 p-5">
                <h4 className="text-sm font-semibold text-gray-900 flex items-center mb-3">
                  <Mail className="w-4 h-4 mr-2 text-blue-600" />
                  Notify Management
                </h4>

                {loadingDaily ? (
                  <div className="animate-pulse space-y-2">
                    <div className="h-4 bg-gray-200 rounded w-3/4" />
                    <div className="h-4 bg-gray-200 rounded w-1/2" />
                  </div>
                ) : (
                  <>
                    {/* Recipient list */}
                    <div className="space-y-2 mb-4">
                      {notificationTargets?.tradeLeader ? (
                        <div className="flex items-center gap-2 text-xs">
                          <User className="w-3.5 h-3.5 text-gray-400" />
                          <span className="text-gray-500 w-32 shrink-0">Trade Leader:</span>
                          <span className="font-medium text-gray-900">{notificationTargets.tradeLeader.name}</span>
                          {notificationTargets.tradeLeader.email && (
                            <span className="text-gray-400">{notificationTargets.tradeLeader.email}</span>
                          )}
                        </div>
                      ) : (
                        <div className="flex items-center gap-2 text-xs text-gray-400">
                          <User className="w-3.5 h-3.5" />
                          <span className="w-32 shrink-0">Trade Leader:</span>
                          <span className="italic">Not assigned</span>
                        </div>
                      )}

                      {notificationTargets?.leadTradeLeader?.length > 0 ? (
                        notificationTargets.leadTradeLeader.map((ltl, i) => (
                          <div key={`ltl-${i}`} className="flex items-center gap-2 text-xs">
                            <User className="w-3.5 h-3.5 text-gray-400" />
                            <span className="text-gray-500 w-32 shrink-0">{i === 0 ? 'Lead Trade Leader:' : ''}</span>
                            <span className="font-medium text-gray-900">{ltl.name}</span>
                            {ltl.email && <span className="text-gray-400">{ltl.email}</span>}
                          </div>
                        ))
                      ) : (
                        <div className="flex items-center gap-2 text-xs text-gray-400">
                          <User className="w-3.5 h-3.5" />
                          <span className="w-32 shrink-0">Lead Trade Leader:</span>
                          <span className="italic">Not assigned</span>
                        </div>
                      )}

                      {notificationTargets?.deputyHod?.length > 0 ? (
                        notificationTargets.deputyHod.map((d, i) => (
                          <div key={`dhod-${i}`} className="flex items-center gap-2 text-xs">
                            <User className="w-3.5 h-3.5 text-gray-400" />
                            <span className="text-gray-500 w-32 shrink-0">{i === 0 ? 'Deputy HOD:' : ''}</span>
                            <span className="font-medium text-gray-900">{d.name}</span>
                            {d.email && <span className="text-gray-400">{d.email}</span>}
                          </div>
                        ))
                      ) : (
                        <div className="flex items-center gap-2 text-xs text-gray-400">
                          <User className="w-3.5 h-3.5" />
                          <span className="w-32 shrink-0">Deputy HOD:</span>
                          <span className="italic">Not assigned</span>
                        </div>
                      )}

                      {notificationTargets?.hod?.length > 0 ? (
                        notificationTargets.hod.map((h, i) => (
                          <div key={`hod-${i}`} className="flex items-center gap-2 text-xs">
                            <User className="w-3.5 h-3.5 text-gray-400" />
                            <span className="text-gray-500 w-32 shrink-0">{i === 0 ? 'HOD:' : ''}</span>
                            <span className="font-medium text-gray-900">{h.name}</span>
                            {h.email && <span className="text-gray-400">{h.email}</span>}
                          </div>
                        ))
                      ) : (
                        <div className="flex items-center gap-2 text-xs text-gray-400">
                          <User className="w-3.5 h-3.5" />
                          <span className="w-32 shrink-0">HOD:</span>
                          <span className="italic">Not assigned</span>
                        </div>
                      )}
                    </div>

                    {/* Notify button */}
                    {hasNotificationTargets ? (
                      <a
                        href={buildMailto()}
                        className="inline-flex items-center px-4 py-2 bg-red-600 hover:bg-red-700 text-white text-sm font-medium rounded-lg transition-colors"
                      >
                        <Mail className="w-4 h-4 mr-2" />
                        Notify via Email
                      </a>
                    ) : (
                      <div className="flex items-center gap-2 text-xs text-amber-600">
                        <AlertTriangle className="w-4 h-4" />
                        <span>No email addresses available for notification targets. Please update employee records.</span>
                      </div>
                    )}
                  </>
                )}
              </div>
            )}
    </Modal>
  );
};

export default EmployeeOvertimeDetailModal;
